import {
	Box,
	Button, Switch, CircularProgress, Dialog,
	DialogActions,
	DialogContent,
	DialogTitle, IconButton,
	Snackbar,
	TextField, Tooltip, Typography,
	makeStyles,
	InputAdornment,
	FormControl,
	FormGroup,
	FormControlLabel,
	Checkbox
} from '@material-ui/core';
import { Image } from '@material-ui/icons';
import { ColorPicker } from 'material-ui-color';
import React, { useEffect, useState } from 'react';
import { deleteNode, getExtraInfo, getSharedNodes, linkTagToNodeId, putExtraField, putExtraInfo, putNodesById, unlinkTagFromNodeId } from '../../services/hierarchy';
import { getUnassignedMeters } from '../../services/meters';
import PlacesAutocomplete from './PlacesAutocomplete';
import { translateDayToEnglish } from './helpers';
//Componentes
import { Alert } from '@mui/material';
import ControlPanel from '../ControlPanel/ControlModal';
import DevicesList from './DevicesList';
import TagsList from './TagsList';
import UsersList from './UsersList';
//Servicios
import { getAutomateFree } from '../../services/plc_automate';
import Resizer from "react-image-file-resizer";
import DeleteIcon from '@material-ui/icons/Delete';
import { borderRadiusBoxes, stateDetailValueSx, themePalette } from '../Dashboards/State/stateTreeStyles';
import {
	detailCardSx,
	detailPillSx,
	webkitScrollbar,
	webkitscrollbarTrack,
} from '../DevicesSettings/styles';

const formatHierarchyBadgeLabel = (value) =>
	String(value || '')
		.split(' ')
		.filter(Boolean)
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');

const HIERARCHY_DEPTH_BY_LEVEL_NAME = {
	raiz: 0,
	empresa: 1,
	zona: 2,
	'localización': 3,
	'sublocalización': 4,
	extra: 5,
	adicional: 6,
};

const getNodeHierarchyDepth = (node) => {
	const parsedLevel = Number(node?.level);
	if (Number.isFinite(parsedLevel) && parsedLevel >= 0) {
		return parsedLevel;
	}

	return HIERARCHY_DEPTH_BY_LEVEL_NAME[node?.level_nombre] ?? 0;
};

const useStyles = makeStyles(theme => ({
	circular: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		padding: '2rem 0',
	},
	panelRoot: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		minHeight: 0,
		backgroundColor: themePalette.surface.panel,
	},
	formLayout: {
		display: 'flex',
		flexDirection: 'column',
		flex: 1,
		minHeight: 0,
		overflow: 'hidden',
	},
	titleContainer: {
		display: 'flex',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		gap: '0.75rem',
		padding: '0.75rem 1rem',
		borderBottom: `1px solid ${themePalette.border.main}`,
		background: `linear-gradient(180deg, ${themePalette.common.white} 0%, ${themePalette.surface.soft} 100%)`,
	},
	titleContent: {
		minWidth: 0,
		display: 'flex',
		flexDirection: 'column',
		gap: '0.18rem',
	},
	titleEyebrow: {
		fontSize: '0.9rem',
		fontWeight: 700,
		color: '#6b7280',
		textTransform: 'uppercase',
		letterSpacing: '0.08em',
		margin: 0,
	},
	panelBody: {
		flex: 1,
		minHeight: 0,
		overflowY: 'scroll',
		padding: '0.75rem 0.9rem',
		display: 'flex',
		flexDirection: 'column',
		rowGap: '0.6rem',
		'&::-webkit-scrollbar': {
			...webkitScrollbar,
		},
		'&::-webkit-scrollbar-track': {
			...webkitscrollbarTrack,
			background: themePalette.status.scrollbarTrackLight,
		},
		'&::-webkit-scrollbar-thumb': {
			background: themePalette.primary.main,
		},
	},
	form: {
		...detailCardSx,
		marginLeft: 0,
		padding: '0.95rem',
		rowGap: '0.9rem',
		boxSizing: 'border-box',
		'& .MuiFormControl-root': {
			width: '100%',
		},
		'& .MuiInputBase-root': {
			color: themePalette.text.secondary,
			border: `1px solid ${themePalette.border.soft}`,
			borderRadius: borderRadiusBoxes,
			backgroundColor: themePalette.common.white,
			boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.6)',
			paddingLeft: '0.75rem',
			paddingRight: '0.75rem',
		},
		'& .MuiInputBase-input': {
			fontSize: '0.95rem',
		},
		'& .MuiInput-underline:before': {
			borderBottom: 'none',
		},
		'& .MuiInput-underline:hover:not(.Mui-disabled):before': {
			borderBottom: 'none',
		},
		'& .MuiInput-underline:after': {
			borderBottom: 'none',
		},
		'& .MuiOutlinedInput-notchedOutline': {
			borderColor: themePalette.border.soft,
		},
	},
	title: {
		color: themePalette.primary.main,
		fontSize: '1.22rem',
		fontWeight: 700,
		lineHeight: 1.2,
		margin: 0,
		wordBreak: 'break-word',
	},
	titleMeta: {
		fontSize: '0.88rem',
		color: themePalette.text.secondary,
		lineHeight: 1.35,
	},
	levelBadge: {
		...detailPillSx,
		flexShrink: 0,
		alignSelf: 'flex-start',
		backgroundColor: 'rgba(13, 148, 136, 0.1)',
		borderColor: 'rgba(13, 148, 136, 0.22)',
		color: '#0f766e',
	},
	label: {
		fontSize: '0.78rem',
		fontWeight: 700,
		color: 'rgb(8, 95, 145)',
		textTransform: 'uppercase',
		letterSpacing: '0.08em',
		margin: 0,
		display: 'block',
		marginTop: theme.spacing(1.1),
	},
	valueText: {
		...stateDetailValueSx,
		marginTop: theme.spacing(0.18),
	},
	inlineLabelRow: {
		display: 'flex',
		alignItems: 'baseline',
		gap: '0.45rem',
		flexWrap: 'wrap',
	},
	input: {
		marginTop: theme.spacing(0.35),
	},
	btnImage: {
		marginTop: theme.spacing(1),
		borderRadius: borderRadiusBoxes,
		textTransform: 'none',
		borderColor: themePalette.border.main,
		color: themePalette.primary.main,
		background: `linear-gradient(180deg, ${themePalette.common.white} 0%, ${themePalette.surface.soft} 100%)`,
	},
	btn: {
		margin: 0,
		minWidth: '120px',
		padding: '0.55rem 1rem',
		borderRadius: borderRadiusBoxes,
		boxShadow: 'none',
		textTransform: 'none',
		fontWeight: 600,
	},
	imageContainer: {
		display: 'flex',
		justifyContent: 'center',
		padding: '0.75rem',
		borderRadius: borderRadiusBoxes,
		border: `1px solid ${themePalette.border.soft}`,
		backgroundColor: themePalette.surface.soft,
	},
	newImage: {
		fontWeight: 400,
		fontSize: '0.85rem',
		lineHeight: 1.5,
		color: themePalette.text.secondary,
	},
	imageFilename: {
		color: themePalette.primary.main,
		fontWeight: 600,
		textDecoration: 'none',
	},
	image: {
		maxWidth: '100%',
		maxHeight: '220px',
		borderRadius: borderRadiusBoxes,
		boxShadow: themePalette.shadow.card,
	},
	imageName: {
		fontSize: '12px',
	},
	scheduleTitle: {
		fontSize: '13px',
		color: theme.palette.grey.main,
		backgroundColor: theme.palette.grey.dark,
		padding: `${theme.spacing(0.5)}px ${theme.spacing(2)}px`,
		borderRadius: theme.spacing(1),
		textAlign: 'center',
	},
	rowContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: theme.spacing(0.5),
		marginBottom: theme.spacing(1),
	},
	dayName: {
		fontSize: 16,
		color: theme.palette.primary.light,
		marginBottom: theme.spacing(1),
	},
	timeSelector: {
		width: '100%',
	},
	buttons: {
		marginBottom: '0.85em',
	},
	sectionDivider: {
		width: '100%',
		height: 1,
		margin: '0.25rem 0 0.15rem',
		backgroundColor: themePalette.border.main,
	},
	stickyActions: {
		display: 'flex',
		justifyContent: 'center',
		gap: '0.75rem',
		flexWrap: 'wrap',
		flexShrink: 0,
		padding: '0.85rem 0.95rem',
		background: themePalette.surface.stickyGradient,
		borderTop: `1px solid ${themePalette.border.main}`,
		zIndex: 2,
		boxShadow: '0 -8px 18px rgba(15, 23, 42, 0.05)',
	},
	actionButton: {
		flex: '1 1 140px',
		minWidth: 0,
		maxWidth: '220px',
	},
	actionButtonDanger: {},
	'@media (max-width: 600px)': {
		panelBody: {
			padding: '0.65rem 0.7rem',
		},
		form: {
			padding: '0.8rem',
			rowGap: '0.8rem',
		},
		btn: {
			minWidth: 0,
			padding: '0.48rem 0.8rem',
			fontSize: '0.92rem',
		},
		stickyActions: {
			justifyContent: 'stretch',
			gap: '0.65rem',
			padding: '0.7rem',
		},
		actionButton: {
			flex: '1 1 calc(50% - 0.325rem)',
			maxWidth: 'none',
		},
		actionButtonDanger: {
			flexBasis: '100%',
		},
	},
	containerGenerator: {
		gap: '1rem',
		"@media (min-width: 700px)": {
			display: 'flex',
			alignItems: 'end',
		},
	},
}));

const EMPTY_ARRAY = [];
const INITIAL_LAPSE = {
	days: [],
	hours: [{
		from: '',
		to: ''
	}]
};

export default function NodeInfo({ setMsg, setClose, alertNodo, selectedNode, setSelectedNode, setTreeChanges, nodes, treeChanges, handleSelectedNode, onNodeUpdate, onNodeDelete, hidePanelHeader = false }) {
	const { level_nombre } = selectedNode;
	const classes = useStyles();
	const blueLine = <Box className={classes.sectionDivider} />;
	const levelBadgeLabel = formatHierarchyBadgeLabel(level_nombre);
	const [loading, setLoading] = useState(false);
	const [nodeData, setNodeData] = useState(selectedNode)
	const selectedNodeId = selectedNode?.id;
	const selectedNodeParent = selectedNode?.parent;
	const selectedNodeLevel = getNodeHierarchyDepth(selectedNode);
	const selectedNodeTags = selectedNode?.tags || EMPTY_ARRAY;
	const [extraField, setExtraField] = useState({});
	const [sharedNodes, setSharedNodes] = useState([]);
	// Serán pasados como props a DevicesList.
	//Medidores
	const [selectedMeterAux, setSelectedMeterAux] = useState({});
	const [selectedMeter, setSelectedMeter] = useState(0);
	const [selectedMeterName, setSelectedMeterName] = useState("");
	const [unassignedMeters, setUnassignedMeters] = useState([]);
	//Deshabilira boton de configurar invertir fases si no esta guardado el mediodor
	const [isSettingsButtonDisabled, setIsSettingsButtonDisabled] = useState(false);
	//Automates
	const [unassignedAutomates, setUnassignedAutomates] = useState([]);
	const [selectedAutomates, setSelectedAutomates] = useState([]);
	// Serán pasados como props a TagsList
	const [selectedTags, setSelectedTags] = useState([]);
	const [deletedTags, setDeletedTags] = useState([]);
	const [brothersInfo, setBrothersInfo] = useState([]);
	const [fatherInfo, setFatherInfo] = useState({});
	const [grandfatherInfo, setGrandfatherInfo] = useState({});
	const [greatGrandfatherInfo, setGreatGrandfatherInfo] = useState({});
	const [greatGreatGrandfatherInfo, setGreatGreatGrandfatherInfo] = useState({});
	const [newImage, setNewImage] = useState({})
	const [newImageName, setNewImageName] = useState("")
	const [newImagePlan, setNewImagePlan] = useState({})
	const [newImagePlanName, setNewImagePlanName] = useState("")
	const [lapses, setLapses] = useState([]);
	//estado de el panel de controles.(ControlModal)
	const [controlPanel, setControlPanel] = useState({})
	//estado para abrir modal de componente ControlPanel
	const [openModal, setOpenModal] = useState(false)
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [selectedHoursTimeSlot] = useState({
		time_from: '00:00',
		time_to: '00:00',
		step: 900
	})
	const [notification, setNotification] = useState({
		open: false,
		message: '',
		severity: 'error',
	});

	useEffect(() => {
		//Obtiene los automate q no estan asignados
		const getAutomateFreeAux = async () => {
			let res = await getAutomateFree()
			setUnassignedAutomates(res?.data)
		}
		getAutomateFreeAux()
	}, [])

	useEffect(() => {
		setNodeData(selectedNode)
	}, [selectedNode])

	useEffect(() => {
		const fetchNodeData = async () => {
			if (!selectedNodeId) return;
			try {
				setLoading(true);
				setSelectedTags(selectedNodeTags);
				setFatherInfo({});
				setGrandfatherInfo({});
				setGreatGrandfatherInfo({});
				setGreatGreatGrandfatherInfo({});
				//ACA TENGO LA RESPUESTA DEL BACK CON EL DATO DE EL MODELO.
				const res1 = await getExtraInfo(selectedNodeId)
				//ACA LA INFO PARA EL MODAL DE  CONTROLES
				setControlPanel(res1.data)
				const { address, telephone, image, plant, workhours, coordinates, people, superficie, meter_name, meter_id } = res1.data;
				setExtraField({ address, telephone, image, plant, workhours, coordinates, people, superficie, meter_name, meter_id })
				setSelectedMeter(meter_id);
				setSelectedMeterName(meter_name);
				let parent1 = null;
				let parent2 = null;
				let parent3 = null;
				let parent4 = null;
				// Si no es empresa
				if (selectedNodeLevel > 1) {
					parent1 = nodes.find(n => n.id === selectedNodeParent)
					setFatherInfo(parent1);
				}
				// Si es una localización, sublocalización o extra
				if (selectedNodeLevel > 2) {
					parent2 = nodes.find(n => n.id === parent1?.parent)
					setGrandfatherInfo(parent2);
				}
				// Solo tengo horas de localización
				if (selectedNodeLevel === 3) {
					if (workhours?.length > 0) {
						setLapses(workhours);
					} else {
						setLapses([INITIAL_LAPSE])
					}

				}
				// Si es una sublocalización o extra
				if (selectedNodeLevel > 3) {
					parent3 = nodes.find(n => n.id === parent2?.parent)
					setGreatGrandfatherInfo(parent3);
				}
				// Si es extra
				if (selectedNodeLevel > 4) {
					parent4 = nodes.find(n => n.id === parent3?.parent)
					setGreatGreatGrandfatherInfo(parent4);
				}

				let brothers = nodes.filter(n => n.parent === selectedNodeParent && n.id !== selectedNodeId);
				setBrothersInfo(brothers);
				setLoading(false);
			}
			catch (error) {
				console.error(error);
				setLoading(false);
			}
		};
		fetchNodeData();
	}, [nodes, selectedNodeId, selectedNodeLevel, selectedNodeParent, selectedNodeTags]);

	useEffect(() => {
		const fetchMetersData = async () => {
			const res = await getUnassignedMeters();
			setUnassignedMeters(res.data);
		};
		fetchMetersData();
	}, [treeChanges]);

	useEffect(() => {
		const fetchSharedNodes = async () => {
			if (!selectedNodeId) return;
			try {
				const res = await getSharedNodes(selectedNodeId);
				setSharedNodes(res.data)
			}
			catch (error) {
				console.error(error);
			}
		}
		fetchSharedNodes()
	}, [selectedNodeId])

	const handleCreateLapse = () => {
		const newLapse = {
			days: [],
			hours: [{
				from: '',
				to: ''
			}],
		}
		const auxLapses = [
			...lapses,
			newLapse
		];
		setLapses(auxLapses);
	};

	const handleRemoveLapse = index => {
		const auxLapses = lapses.filter((lapse, i) => {
			return i !== index;
		});
		setLapses(auxLapses);
	};

	const handleColor = (value) => {
		try {
			if (value.css.backgroundColor.length === 7) {
				setNodeData({ ...nodeData, color: value.css.backgroundColor });
			}
			else {
				setNodeData({ ...nodeData, color: 'black' });
			}

		} catch (error) {
			console.error(error)
		}
	};

	const getErrors = () => {
		let errors = false;
		lapses.forEach(lapse => {
			if (lapse.startError || lapse.stopError) {
				errors = true;
			}
		});
		return errors;
	};

	const handleChange = e => {
		setNodeData({ ...nodeData, [e.target.name]: e.target.value });
	};
	const handleCancel = e => {
		setSelectedNode({});
	};
	const handleDelete = e => {
		setConfirmOpen(true);
	};

	const resizeFile = (file) =>
		new Promise((resolve) => {
			Resizer.imageFileResizer(
				file,
				1024,
				768,
				"JPEG",
				80,
				0,
				(uri) => {
					resolve(uri);
				},
				'file'
			);
		});


	const handleSubmit = async e => {
		e.preventDefault();
		try {
			setLoading(true);
			const ids = selectedAutomates?.map(item => item.id);
			//Actualizo los valores ingresados
			await putNodesById(selectedNode.id, {
				parent: selectedNode.parent,
				nombre: nodeData.nombre,
				descripcion: nodeData.descripcion,
				medidor_virtual: selectedMeter ? false : true,
				medidor: selectedMeter ? selectedMeter : null,
				color: nodeData.color,
				es_generador: nodeData.es_generador,
				es_bidireccional: nodeData.es_bidireccional,
				es_medicion_no_medida: nodeData.es_medicion_no_medida,
				potencia_generacion: controlPanel.potencia_generacion,
				potencia_acople: controlPanel.potencia_acople,
				automates: ids
			});
			const auxObject = {
				hours: {
					friday: [],
					monday: [],
					sunday: [],
					tuesday: [],
					saturday: [],
					thursday: [],
					wednesday: [],
				},
			};
			lapses.forEach(lapse => {
				if (lapse.start || lapse.stop)
					auxObject.hours[translateDayToEnglish(lapse.day)].push({
						start: lapse.start,
						stop: lapse.stop,
					});
			});
			let workhours = getLapsesWithoutEmptyValues();
			await putExtraInfo(selectedNode.id, {
				address: extraField?.address,
				telephone: extraField?.telephone,
				coordinates: extraField?.coordinates,
				people: extraField?.people,
				superficie: extraField?.superficie,
				workhours
			})
			if (newImageName) {
				await putExtraField(selectedNode.id, 'image', newImage);
				setNewImage({});
				setNewImageName('');
			}
			if (newImagePlanName) {
				await putExtraField(selectedNode.id, 'floor_plan', newImagePlan);
				setNewImagePlan({});
				setNewImagePlanName('');
			}
			if (deletedTags.length !== 0) {
				// Elimino de la DB los tags que ya no están seleccionados
				for (const tag of deletedTags) {
					await unlinkTagFromNodeId(selectedNode.id, { tag_id: tag.id });
				}
			}
			setDeletedTags([]);
			// Agrego a la DB los tags que están seleccionados (si ya tenía seleccionado uno y aunque no haya habido cambios vuelvo a seleccionarlo)
			selectedTags.forEach(async tag => {
				await linkTagToNodeId(selectedNode.id, { tag_id: tag.id });
			});
			const updatedNode = {
				...selectedNode,
				...nodeData,
				medidor: selectedMeter ? selectedMeter : null,
				medidor_virtual: !selectedMeter,
				automates: selectedAutomates,
				tags: selectedTags,
				has_address: selectedNode.level_nombre === 'localización' ? Boolean(extraField?.address) : selectedNode.has_address,
			};
			onNodeUpdate?.(updatedNode);
			setSelectedNode(updatedNode);
			setLoading(false);
			setClose(true)
			setMsg({
				msg: "Los datos del nodo se modificaron correctamente",
				type: 'success'
			})
			// Quiero volver a renderizar el árbol de dispositivos a raíz de este cambio.
			setTreeChanges(current => !current);
		}
		catch (error) {
			console.error(error);
			setClose(true)
			setMsg({
				msg: error?.response?.data?.msg || 'Ocurrio un error',
				type: 'error'
			})
			setLoading(false);
		}
		handleSelectedNode(selectedNode)
		setIsSettingsButtonDisabled(false)
	};

	const handleConfirmClose = () => {
		setConfirmOpen(false);
	};

	const handleConfirmDelete = async () => {
		setConfirmOpen(false);
		setSelectedNode({});
		alertNodo()

		//Llamo a DELETE Endpoint
		try {
			setLoading(true);
			//Actualizo los valores ingresados
			await deleteNode(selectedNode.id);
			onNodeDelete?.(selectedNode.id);

			setLoading(false);
			// Quiero volver a renderizar el árbol de dispositivos a raíz de este cambio.
			setTreeChanges(current => !current);
		} catch (error) {
			console.error(error);
		}
	};

	const handleIsGeneratorChange = (e) => {
		setNodeData({
			...nodeData,
			es_generador: e.target.checked
		})
		if (!e.target.checked) {
			setControlPanel({
				...controlPanel,
				potencia_generacion: 0,
				potencia_acople: 0
			})
		}
	};

	const handleIsBidirectionalChange = (e) => {
		setNodeData({
			...nodeData,
			es_bidireccional: e.target.checked
		})
	};

	const handleChangeForControlPanel = e => {
		setControlPanel({ ...controlPanel, [e.target.name]: e.target.value });
	};

	const handleTimeSlot = (event, index) => {
		const { value, checked } = event.target;
		let auxLapses = [...lapses];
		if (checked) {
			auxLapses[index] = {
				...auxLapses[index],
				days: [...(auxLapses[index].days || []), value]
			};
		} else {
			auxLapses[index] = {
				...auxLapses[index],
				days: auxLapses[index].days?.filter(day => day !== value)
			};
		}
		setLapses(auxLapses);
	};

	const handleHoursTimeSlot = (event, indexLapse, indexHour) => {
		const { value, name } = event.target;
		let auxLapses = [...lapses];
		let hoursOfLapse = auxLapses[indexLapse].hours;
		hoursOfLapse[indexHour] = {
			...hoursOfLapse[indexHour],
			[name]: value
		}
		auxLapses[indexLapse] = {
			...auxLapses[indexLapse],
			hours: hoursOfLapse
		}
		setLapses(auxLapses);
	}

	const getLapsesWithoutEmptyValues = () => {
		let auxLapses = [...lapses];
		if (auxLapses.length === 0) return [];
		let lapsesWithoutEmptyValues = auxLapses.map(lapse => {
			return {
				...lapse,
				hours: lapse.hours.filter(hour => hour.from !== '' && hour.to !== '')
			}
		}).filter(lapse => lapse.days.length > 0);
		return lapsesWithoutEmptyValues;
	}

	const handleUnmeteredNode = (e) => {
		setNodeData({
			...nodeData,
			es_medicion_no_medida: e.target.checked,
			es_generador: false,
			es_bidireccional: false
		})
	}

	const someBrotherHasMeter = () => {
		if (!brothersInfo || brothersInfo.length === 0) return false;
		let brotherHasMeter = brothersInfo.find(brother => brother.medidor);
		return brotherHasMeter ? true : false;
	}

	const someBrotherHasUnmetered = () => {
		if (!brothersInfo || brothersInfo.length === 0) return false;
		let brotherHasUnmetered = brothersInfo.find(brother => brother.es_medicion_no_medida);
		return brotherHasUnmetered ? true : false;
	}

	const someBrotherHasChildrenWithMeter = () => {
		if (!brothersInfo || brothersInfo.length === 0) return false;
		const nephewsWithMeter = brothersInfo.flatMap(brother =>
			nodes.filter(node => node.parent === brother.id && node.medidor)
		);
		return nephewsWithMeter.length > 0;
	};


	return (
		<>
			{/* Modal de configuracion de fases */}

			<ControlPanel
				openModal={openModal}
				setOpenModal={setOpenModal}
				data={controlPanel}
				selectedNode={selectedNode}
				selectedMeterAux={selectedMeterAux}
			/>

			{loading &&
				<Box className={classes.circular}>
					<CircularProgress />
				</Box>
			}
			{!loading &&
				<>
					<Box component='form' onSubmit={handleSubmit} className={classes.panelRoot}>
						{!hidePanelHeader && <Box className={classes.titleContainer}>
							<Box className={classes.titleContent}>
								<Box component='span' className={classes.titleEyebrow}>
									Configuracion del nodo
								</Box>
								<Typography variant='h4' className={classes.title}>
									{nodeData.nombre}
								</Typography>
								<Box component='span' className={classes.titleMeta}>
									{selectedNode?.id ? `Nodo #${selectedNode.id}` : ''}
								</Box>
							</Box>
							<Box component='span' className={classes.levelBadge}>
								{levelBadgeLabel}
							</Box>
						</Box>}
						<Box className={classes.formLayout}>
							<Box className={classes.panelBody}>
								<Box className={classes.form}>
						{level_nombre === 'zona' && (
							<>
								<Box component='label' display='block' className={classes.label}>
									Empresa
								</Box>
								<Box className={classes.valueText}>{fatherInfo?.nombre || '-'}</Box>
							</>
						)}
						{level_nombre === 'localización' && (
							<>
								<Box component='label' display='block' className={classes.label}>
									Empresa
								</Box>
								<Box className={classes.valueText}>{grandfatherInfo?.nombre || '-'}</Box>
								<Box component='label' display='block' className={classes.label}>
									Zona
								</Box>
								<Box className={classes.valueText}>{fatherInfo?.nombre || '-'}</Box>
							</>
						)}
						{level_nombre === 'sublocalización' && (
							<>
								<Box component='label' display='block' className={classes.label}>
									Empresa
								</Box>
								<Box className={classes.valueText}>{greatGrandfatherInfo?.nombre || '-'}</Box>
								<Box component='label' display='block' className={classes.label}>
									Zona
								</Box>
								<Box className={classes.valueText}>{grandfatherInfo?.nombre || '-'}</Box>
								<Box component='label' display='block' className={classes.label}>
									Localización
								</Box>
								<Box className={classes.valueText}>{fatherInfo?.nombre || '-'}</Box>
							</>
						)}
						{level_nombre === 'extra' && (
							<>
								<Box component='label' display='block' className={classes.label}>
									Empresa
								</Box>
								<Box className={classes.valueText}>{greatGreatGrandfatherInfo?.nombre || '-'}</Box>
								<Box component='label' display='block' className={classes.label}>
									Zona
								</Box>
								<Box className={classes.valueText}>{greatGrandfatherInfo?.nombre || '-'}</Box>
								<Box component='label' display='block' className={classes.label}>
									Localización
								</Box>
								<Box className={classes.valueText}>{grandfatherInfo?.nombre || '-'}</Box>
								<Box component='label' display='block' className={classes.label}>
									Sublocalización
								</Box>
								<Box className={classes.valueText}>{fatherInfo?.nombre || '-'}</Box>
							</>
						)}

						<Box component='label' display='block' className={classes.label}>
							Nombre *
						</Box>
						<TextField
							fullWidth
							placeholder='Nombre'
							name='nombre'
							value={nodeData.nombre || ''}
							onChange={e => handleChange(e)}
							color='primary'
							className={classes.input}
						/>
						<Box component='label' display='block' className={classes.label}>
							Descripcion *
						</Box>
						<TextField
							fullWidth
							placeholder='Descripcion'
							name='descripcion'
							value={nodeData.descripcion || ''}
							onChange={e => handleChange(e)}
							color='primary'
							className={classes.input}
						/>

						{(selectedNode.level_nombre === "sublocalización" || selectedNode.level_nombre === "extra") &&
							<Box className={classes.containerGenerator}>
								<Box>
									<Box component='label' display='block' className={classes.label}>
										Es generador *
									</Box>
									<Switch
										checked={nodeData.es_generador}
										color='primary'
										name='is_generator'
										onChange={handleIsGeneratorChange}
										disabled={nodeData.es_medicion_no_medida}
									/>
								</Box>

								<Box style={{ marginBottom: 5 }}>
									<Tooltip style={{ marginLeft: 15 }} title='Potencia de generacion: Es la potencia total de todos los paneles solares fotovoltaicos sumada.' aria-label='Potencia de generacion'>
										<TextField
											placeholder='Potencia de generacion'
											name='potencia_generacion'
											value={controlPanel.potencia_generacion || ''}
											onChange={e => handleChangeForControlPanel(e)}
											disabled={!nodeData.es_generador}
											color='primary'
											className={classes.input}
											InputProps={{
												endAdornment: <InputAdornment position='end'>kW</InputAdornment>,
											}}
										/>
									</Tooltip>
									<Tooltip style={{ marginLeft: 15 }} title='Potencia de acople: Es la potencia nominal total de todos los inversores solares fotovoltaicos sumados.' aria-label='Potencia de acople'>
										<TextField
											placeholder='Potencia de acople'
											name='potencia_acople'
											value={controlPanel.potencia_acople || ''}
											onChange={e => handleChangeForControlPanel(e)}
											disabled={!nodeData.es_generador}
											color='primary'
											className={classes.input}
											InputProps={{
												endAdornment: <InputAdornment position='end'>kW</InputAdornment>,
											}}
										/>
									</Tooltip>
								</Box>
							</Box>
						}

						{selectedNode?.level_nombre === "localización" &&
							<Box className={classes.containerGenerator}>
								<Box>
									<Box component='label' display='block' className={classes.label}>
										Es bidireccional
									</Box>
									<Box display={'flex'} alignItems={'center'}>
										<Switch
											checked={nodeData?.es_bidireccional}
											color='primary'
											name='is_bidirectional'
											disabled={!nodeData?.es_bidireccional_allow_update || nodeData?.es_medicion_no_medida}
											onChange={handleIsBidirectionalChange}
										/>
										<Box>
											{nodeData?.es_bidireccional ? 'Si' : 'No'}
										</Box>
									</Box>
								</Box>
							</Box>
						}

						<Box className={classes.containerGenerator}>
							<Box>
								<Box component='label' display='block' className={classes.label}>
									No medido
								</Box>
								<Box display={'flex'} alignItems={'center'}>
									<Switch
										checked={nodeData?.es_medicion_no_medida}
										color='primary'
										name='is_unmetered'
										disabled={!someBrotherHasChildrenWithMeter() && (!selectedNode.virtual || selectedNode.medidor || selectedNode.hijos?.length > 0 || !fatherInfo?.medidor || !someBrotherHasMeter() || someBrotherHasUnmetered())}
										onChange={handleUnmeteredNode}
									/>
									<Box>
										{nodeData?.es_medicion_no_medida ? 'Si' : 'No'}
									</Box>
								</Box>
							</Box>
						</Box>


						{/* Si es una localización muestra los inputs de direccion/teléfono y si no lo es los oculto. */}
						{selectedNode.level_nombre === "localización" &&
							<Box>
								<Box component='label' display='block' className={classes.label}>
									Direccion *
								</Box>
								<PlacesAutocomplete setExtraField={setExtraField} extraField={extraField} />

								<Box component='label' display='block' className={classes.label}>
									Telofono *
								</Box>
								<TextField
									fullWidth
									placeholder='Telofono'
									name='telefono'
									value={extraField.telephone || ''}
									onChange={e =>
										setExtraField({ ...extraField, telephone: e.target.value })
									}
									color='primary'
									className={classes.input}
								/>

								<Box component='label' display='block' className={classes.label}>
									Superficie *
								</Box>
								<TextField
									fullWidth
									placeholder='Superficie'
									name='superficie'
									value={extraField.superficie || ''}
									onChange={e =>
										setExtraField({ ...extraField, superficie: e.target.value })
									}
									color='primary'
									className={classes.input}
								/>
								<Box component='label' display='block' className={classes.label}>
									Cantidad de Empleados *
								</Box>
								<TextField
									fullWidth
									placeholder='Cantidad de Empleados'
									name='telefono'
									value={extraField.people || ''}
									onChange={e =>
										setExtraField({ ...extraField, people: e.target.value })
									}
									color='primary'
									className={classes.input}
								/>

								<Box component='label' display='block' className={classes.label}>
									Imagen
								</Box>
								{
									// Si cargué una nueva imagen
									newImageName &&
									<Box component='label' display='block' className={classes.label}>
										<Box className={classes.newImage}>
											Nueva imagen seleccionada: <span className={classes.imageFilename}>{newImageName}</span>. Es necesario guardar los cambios para confirmarla.
										</Box>
									</Box>
								}

								{
									// Si no cargué una nueva imagen y obtuve una de la API
									!newImageName && extraField.image &&

									<Box className={classes.imageContainer}>

										<img
											src={`${extraField.image}`}
											className={classes.image}
											alt='Imagen'
										/>
									</Box>
								}

								{
									!newImageName &&
									<Button
										variant='outlined'
										color='primary'
										component='label'
										fullWidth
										startIcon={<Image />}
										className={classes.btnImage}
									>
										Seleccionar
										<input
											type='file'
											onChange={async e => {

												try {
													const file = e.target.files[0];
													setNewImageName(file.name);

													const image = await resizeFile(file);

													const formData = new FormData();
													formData.append('imagen', image);
													setNewImage(formData);
												} catch (err) {
													console.error(err);
												}

											}}
											accept="image/png, image/jpeg"
											hidden
										/>
									</Button>
								}

								{blueLine}

								<Box display={'flex'} justifyContent={'space-between'} alignItems={'start'}>
									<Box>
										<Box component='label' display='block' className={classes.label}>
											Horario Laboral
										</Box>
									</Box>

									<Button color='primary' style={{ margin: '10px' }} variant='contained' onClick={() => handleCreateLapse()}>
										Agregar franja horaria
									</Button>
								</Box>

								{lapses.length > 0 && lapses.map((lapse, indexLapse) => (
									<Box key={indexLapse} display={'flex'} alignItems={'start'} justifyContent={'space-between'} mb={2} width={'100%'}>
										<Box width={'100%'} x>
											<Box display={'flex'} alignItems={'start'} justifyContent={'space-between'} width={'100%'}>
												<FormControl>
													<FormGroup row>
														{['lu', 'ma', 'mi', 'ju', 'vi', 'sa', 'do'].map((elem) => (
															<FormControlLabel
																key={elem}
																control={
																	<Checkbox
																		value={elem}
																		checked={lapse.days?.includes(elem)}
																		onChange={(event) => handleTimeSlot(event, indexLapse)}
																		color="primary"
																	/>
																}
																label={elem}
																labelPlacement="start"
															/>
														))}
													</FormGroup>
												</FormControl>

												<Tooltip title={`Eliminar franja horaria`} aria-label='Eliminar franja horaria'>
													<IconButton aria-label='eliminar franja horaria' onClick={() => handleRemoveLapse(indexLapse)}>
														<DeleteIcon color='secondary' />
													</IconButton>
												</Tooltip>
											</Box>

											{lapse.hours && lapse.hours.map((hour, indexHour) => {
												return (
													<Box my={2} display={'flex'} key={indexHour}>
														{/* DESDE */}
														<Box >
															<TextField
																name="from"
																type='time'
																label='Desde:'
																size='small'
																variant='outlined'
																value={hour.from}
																// disabled={disabledInputFunction()}
																onChange={e => handleHoursTimeSlot(e, indexLapse, indexHour)}
																InputLabelProps={{
																	shrink: true,
																}}
																inputProps={{
																	step: selectedHoursTimeSlot?.step,
																	pattern: "[0-9]{2}:[0-9]{2}",
																}}
																onInput={(e) => {
																	const input = e.target;
																	let value = input.value;
																	// Solo bloquear los minutos si el step es 3600 (1 hora)
																	if (selectedHoursTimeSlot?.step === 3600 && value) {
																		const [hours] = value.split(":");
																		input.value = `${hours}:00`; // Fija los minutos a 00
																		handleHoursTimeSlot(e, indexLapse, indexHour); // Llama a la función de manejo
																	} else {
																		// Si el step no es 3600, permitir la modificación normal
																		handleHoursTimeSlot(e, indexLapse, indexHour);
																	}
																}}
															/>
														</Box>
														{/* HASTA */}
														<Box ml={2}>
															<TextField
																name="to"
																type="time"
																label="Hasta:"
																size="small"
																variant="outlined"
																value={hour.to}
																onChange={e => handleHoursTimeSlot(e, indexLapse, indexHour)}
																InputLabelProps={{
																	shrink: true,
																}}
																inputProps={{
																	step: selectedHoursTimeSlot?.step,
																	pattern: "[0-9]{2}:[0-9]{2}",
																}}
																onInput={(e) => {
																	const input = e.target;
																	let value = input.value;

																	// Solo bloquear los minutos si el step es 3600 (1 hora)
																	if (selectedHoursTimeSlot?.step === 3600 && value) {
																		const [hours] = value.split(":");
																		input.value = `${hours}:00`; // Fija los minutos a 00
																		handleHoursTimeSlot(e, indexLapse, indexHour); // Llama a la función de manejo
																	} else {
																		// Si el step no es 3600, permitir la modificación normal
																		handleHoursTimeSlot(e, indexLapse, indexHour);
																	}
																}}
															/>
														</Box>
													</Box>
												)
											})
											}
										</Box>
									</Box>
								))}

							</Box>
						}

						{blueLine}

						{selectedNodeLevel > 2 && (
							<>
								<DevicesList
									// Ambas props tienen el mismo valor pero cuando modifique en DeviceList quiero conservar el original
									//medidor
									nodeData={nodeData}
									savedMeter={extraField.meter_id}
									savedMeterName={extraField.meter_name}
									selectedMeter={selectedMeter}
									selectedMeterName={selectedMeterName}
									unassignedMeters={unassignedMeters}
									setSelectedMeter={setSelectedMeter}
									setSelectedMeterName={setSelectedMeterName}
									//automates									
									setSelectedAutomates={setSelectedAutomates}
									selectedAutomates={selectedAutomates}
									unassignedAutomates={unassignedAutomates}
									setUnassignedAutomates={setUnassignedAutomates}
									setSelectedMeterAux={setSelectedMeterAux}
									setIsSettingsButtonDisabled={setIsSettingsButtonDisabled}
								/>

							
								{/* Configuracion de fases */}
								{controlPanel?.product &&
									<>
										<Box className={classes.inlineLabelRow}>
											<Box component='span' className={classes.label}>
												Modelo:
											</Box>
											<Box component='span' className={classes.valueText}>
												{nodeData?.realtime?.producto ? nodeData?.realtime?.producto : controlPanel?.product}
											</Box>
										</Box>

										<Box mt={2} mb={2} >
											<Tooltip title={`Aqui puede modificar el estado de las fases que pertenecen al dispositivo`}>
												<Button
													onClick={() => { setOpenModal(true) }}
													size="small"
													color='primary'
													variant='contained'
													disabled={isSettingsButtonDisabled}
												>
													Configurar
												</Button>
											</Tooltip>
										</Box>
										<Box style={{ color: 'red', marginBottom: 10 }}>
											{isSettingsButtonDisabled && 'Debe guardar su configuración de medidor para habilitar el botón de configurar fases'}
										</Box>
										{blueLine}
									</>
								}

								<Box component='label' display='block' className={classes.label}>
									Etiquetas
									<TagsList
										savedTags={selectedNode.tags}
										setDeletedTags={setDeletedTags}
										selectedTags={selectedTags}
										setSelectedTags={setSelectedTags}
									/>
								</Box>
								<Box component='label' display='block' className={classes.label}>
									Color
									<ColorPicker value={nodeData.color} defaultValue="black" onChange={value => handleColor(value)} />
								</Box>

								{blueLine}

								{/* Usuarios compartidos */}
								<Box component='label' display='block' className={classes.label}>
									Usuarios Compartidos
								</Box>
								<UsersList shared={sharedNodes} />

							</>
						)}

								</Box>
							</Box>
							<Box className={classes.stickyActions}>
								<Button
									type='submit'
									color='primary'
									variant='contained'
									className={`${classes.btn} ${classes.actionButton}`}
									disabled={getErrors()}
								>
									Guardar
								</Button>
								<Button
									variant='contained'
									onClick={handleCancel}
									className={`${classes.btn} ${classes.actionButton}`}
								>
									Cancelar
								</Button>
								<Button
									color='secondary'
									variant='contained'
									onClick={handleDelete}
									className={`${classes.btn} ${classes.actionButton} ${classes.actionButtonDanger}`}
								>
									Eliminar
								</Button>
							</Box>
						</Box>
					</Box>

					<Dialog open={confirmOpen} onClose={handleConfirmClose} aria-labelledby='form-dialog-title'>
						<DialogTitle id='form-dialog-title'>¿Desea eliminar el nodo?</DialogTitle>
						<DialogContent>En caso de confirmar el nodo {nodeData.nombre} será eliminado</DialogContent>
						<DialogActions>
							<Button color='default' onClick={handleConfirmClose}>
								Cancelar
							</Button>
							<Button type='submit' color='secondary' variant='outlined' onClick={handleConfirmDelete}>
								Aceptar
							</Button>
						</DialogActions>
					</Dialog>

					<Snackbar
						open={notification.isOpen}
						autoHideDuration={2000}
						onClose={() => setNotification({ ...notification, isOpen: false })}
					>
						<Alert
							severity={notification.severity}
							onClose={() => setNotification(false)}
						>
							{notification.msg}
						</Alert>
					</Snackbar>
				</>
			}
		</>
	);
}



